import {
    HERO_DATA,
    ABOUT_DATA,
    SKILLS_DATA,
    PROJECTS_DATA,
    EXPERIENCE_DATA,
    EDUCATION_DATA,
    CONTACT_DATA,
    PHILOSOPHY_DATA,
} from "@/lib/data";

/**
 * Smart Terminal Intelligence
 * ===========================
 * A client-side "brain" that answers queries with a 20-year-old pro-dev persona.
 * Casual, sharp, enthusiastic, and direct.
 */

// --- Tone & Persona Configuration ---
const PERSONA_PREFIXES = {
    info: ["Here's the tea 🍵:", "Check this out:", "FYI:", "Got you covered:"],
    success: ["Boom! 💥", "Done deal.", "Sorted.", "Easy peasy."],
    error: ["Oof, my bad.", "Yikes, didn't catch that.", "Hmm, try again?"],
    thinking: ["Thinking...", "Hold up...", "Let me check..."],
};

// --- Helper: Format List Items ---
const formatList = (items: string[]) => items.map(i => `• ${i}`).join("\n");

// --- Helper: Fuzzy Match (Simple inclusion for now) ---
const matches = (input: string, keywords: string[]) =>
    keywords.some((k) => input.toLowerCase().includes(k.toLowerCase()));

// --- Main Response Logic ---
export function getSmartResponse(input: string): string {
    const lower = input.toLowerCase().trim();

    // 1. GREETINGS
    if (matches(lower, ["hello", "hi", "hey", "yo", "sup", "greetings"])) {
        return `Yo! 👋 I'm ${HERO_DATA.name}. Developer, builder, and tech enthusiast. \n\nAsk me about my **projects**, **skills**, or just say **hi**!`;
    }

    // 2. WHO ARE YOU / BIO
    if (
        matches(lower, [
            "who are you",
            "about",
            "intro",
            "bio",
            "tell me about yourself",
            "profile",
        ])
    ) {
        return `### About Me 👨‍💻\n\n${ABOUT_DATA.intro}\n\nCurrently: ${ABOUT_DATA.currentFocus} 🚀\n\n📍 Based in: ${ABOUT_DATA.location}`;
    }

    // 3. SKILLS / TECH STACK
    if (matches(lower, ["skills", "tech", "stack", "react", "flutter", "languages", "tools"])) {
        // Check for specific tech queries
        const techMap = {
            flutter: "Flutter is my go-to for mobile dev. Love the hot reload! 💙",
            react: "React is awesome for web. Component-based architecture FTW. ⚛️",
            next: "Next.js gives me super powers on the web. server-side rendering is 🔥.",
            node: "Node.js allows me to run JS everywhere. Backend logic? Sorted. 🟢",
        };

        for (const [key, value] of Object.entries(techMap)) {
            if (lower.includes(key)) return value;
        }

        // Default skills listing
        let response = "### My Tech Arsenal 🛠️\n\n";
        SKILLS_DATA.forEach((category) => {
            const topSkills = category.skills.slice(0, 3).map((s) => s.name).join(", ");
            response += `**${category.title}**: ${topSkills} (+${category.skills.length - 3} more)\n`;
        });
        return response + "\nType `detailed skills` if you want the full list!";
    }

    if (matches(lower, ["detailed skills"])) {
        let response = "### Full Skill Breakdown 📊\n\n";
        SKILLS_DATA.forEach((category) => {
            response += `**${category.title}**\n`;
            response += formatList(category.skills.map(s => `${s.name} (${s.level}%)`)) + "\n\n";
        });
        return response;
    }

    // 4. PROJECTS
    if (matches(lower, ["projects", "work", "portfolio", "built", "apps"])) {
        // Check for specific project queries
        const project = PROJECTS_DATA.find((p) =>
            lower.includes(p.title.toLowerCase()) ||
            lower.includes(p.id?.toLowerCase() || "") ||
            (p.tagline && lower.includes(p.tagline.toLowerCase()))
        );

        if (project) {
            return `### 🚀 ${project.title}\n\n_${project.tagline}_\n\n${project.description}\n\n**Tech Stack**: ${project.tags.join(", ")}\n\n🔗 ${project.link || project.githubUrl || "No link available"}`;
        }

        // List top projects
        let response = "### Top Projects 🏆\n\n";
        PROJECTS_DATA.slice(0, 3).forEach((p) => {
            response += ` - **${p.title}**\n   _${p.tagline}_\n\n`;
        });
        return response + "\nType `all projects` to see everything!";
    }

    if (matches(lower, ["all projects"])) {
        let response = "### All Projects 📁\n\n";
        PROJECTS_DATA.forEach((p) => {
            response += `**${p.title}** - ${p.tagline}\n`;
        });
        return response;
    }

    // 5. EXPERIENCE
    if (matches(lower, ["experience", "job", "career", "work history", "resume"])) {
        let response = "### Career Timeline 💼\n\n";
        EXPERIENCE_DATA.forEach((exp) => {
            response += `**${exp.role}** @ ${exp.company} (${exp.period})\n_${exp.description}_\n\n`;
        });
        return response;
    }

    // 6. CONTACT
    if (matches(lower, ["contact", "email", "social", "reach", "hire"])) {
        return `### Let's Connect! 🤝\n\n📧 Email: ${CONTACT_DATA.email}\n📱 WhatsApp: ${CONTACT_DATA.whatsappNumber}\n\nOr find me on:\n${CONTACT_DATA.socials.map(s => `• [${s.name}](${s.href})`).join("\n")}`;
    }

    // 7. EDUCATION
    if (matches(lower, ["education", "study", "college", "degree", "school"])) {
        let response = "### Academic Background 🎓\n\n";
        EDUCATION_DATA.forEach((edu) => {
            response += `**${edu.degree}**\n${edu.school} (${edu.year})\n\n`;
        });
        return response;
    }

    // 8. PHILOSOPHY
    if (matches(lower, ["philosophy", "values", "principles", "believe"])) {
        let response = "### My Dev Philosophy 🧠\n\n";
        PHILOSOPHY_DATA.forEach((p) => {
            response += `**${p.title}**: ${p.description}\n`;
        });
        return response;
    }

    // 9. FUN / CASUAL
    if (matches(lower, ["joke", "funny"])) {
        const jokes = [
            "Why do programmers prefer dark mode? Because light attracts bugs! 🐛",
            "I told my CSS joke to a backend developer, but they didn't get the style. 🎨",
            "There are 10 types of people in the world: those who understand binary, and those who don't. 01",
        ];
        return jokes[Math.floor(Math.random() * jokes.length)];
    }

    if (matches(lower, ["status", "doing"])) {
        const statuses = [
            "Crushing bugs 🐛",
            "Building the future 🚀",
            "Caffeinating ☕",
            "Thinking about complex systems 🧠",
        ];
        return `Currently: ${statuses[Math.floor(Math.random() * statuses.length)]}`;
    }

    // 10. UNKNOWN / HELP
    return "Hmm, not sure about that one. 🤔\n\nTry asking about:\n• **projects**\n• **skills**\n• **experience**\n• **contact**\n\nOr type `help` for commands!";
}
